import React, { useRef, useState } from "react";
import { Upload, Loader2, X, Link as LinkIcon } from "lucide-react";
import { uploadImage, imgUrl } from "../lib/api";

// Image field with file upload (Emergent Object Storage) + optional URL input
const ImageUpload = ({ value, onChange, label = "Görsel", testId = "image-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [drag, setDrag] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Yükleme başarısız / Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testId}>
      <label className="block text-sm font-medium text-navy-900 mb-1.5">{label}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative flex items-center gap-4 rounded-xl border-2 border-dashed p-4 cursor-pointer transition-colors ${
          drag ? "border-amber-500 bg-amber-50" : "border-slate-300 hover:border-amber-400 bg-slate-50"
        }`}
        data-testid={`${testId}-dropzone`}
      >
        {value ? (
          <img src={imgUrl(value)} alt="preview" className="h-24 w-36 rounded-lg object-cover bg-white" data-testid={`${testId}-preview`} />
        ) : (
          <div className="h-24 w-36 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-300">
            <Upload className="w-7 h-7" />
          </div>
        )}
        <div className="flex-1 text-sm">
          {uploading ? (
            <span className="inline-flex items-center gap-2 text-amber-600"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</span>
          ) : (
            <>
              <p className="font-medium text-navy-900">Bilgisayardan görsel seç veya sürükle-bırak</p>
              <p className="text-slate-500 mt-1">JPG, PNG, WEBP, SVG · maks. 8MB</p>
            </>
          )}
          {error && <p className="text-red-500 mt-1" data-testid={`${testId}-error`}>{error}</p>}
        </div>
        {value && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-red-500"
            title="Kaldır"
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <LinkIcon className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="veya görsel URL'si yapıştırın (https://...)"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-navy-900 outline-none focus:border-amber-500"
          data-testid={`${testId}-url`}
        />
      </div>
    </div>
  );
};

export default ImageUpload;
